import React, { useEffect, useState } from "react";
import "./OfflineSubCompleted.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "./../../axios";
import { toast } from "react-toastify";
import {
  Trophy,
  FileText,
  Loader2,
  Save,
  Send,
  RefreshCw,
  Unlock,
  Lock,
  CheckCircle2,
  FileClock,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";

function OfflineSubDraftCompleted() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { assessmentDetails } = location.state || {};

  // ─── Loading States ──────────
  const [isInProgress, setIsInProgress] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);

  // ─── Mode & Re-evaluation ──────────
  const [mode, setMode] = useState("create"); // 'create' | 'draft' | 'completed'
  const [reevaluating, setReevaluating] = useState(false);
  const [loading, setLoading] = useState(true);

  // ─── Data States ──────────
  const [studentList, setStudentList] = useState([]);
  const [studentListByAssessmentId, setStudentListByAssessmentId] = useState(
    [],
  );
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [originalSelectedStudents, setOriginalSelectedStudents] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [existingAssessmentId, setExistingAssessmentId] = useState(null);

  // ─── Config from state ──────────
  const entryType =
    assessmentDetails?.entryType || state?.assessment?.entryType || "Marks";
  const gradeScaleId =
    assessmentDetails?.gradeScaleId || state?.assessment?.gradeScaleId || null;
  const eventName =
    state?.assessmentDetails?.event_id?.name ||
    state?.assessment?.assessmentEventId?.name ||
    "N/A";
  const typeName =
    state?.assessmentDetails?.type_id?.name ||
    state?.assessment?.assessmentTypeId?.name ||
    "N/A";

  const standard_id =
    assessmentDetails?.standard_id?.id || state?.assessment?.standard_id?.id;
  const section_id =
    assessmentDetails?.section_id?.id || state?.assessment?.section_id?.id;
  const subject_id =
    assessmentDetails?.subject_id?.id || state?.assessment?.subject_id?.id;
  const assessmentTypeId =
    assessmentDetails?.type_id?.id || state?.assessment?.assessmentTypeId;
  const eventId =
    assessmentDetails?.event_id?.id || state?.assessment?.assessmentEventId;

  let timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );
  const academicSessionId = JSON.parse(
    localStorage.getItem("academicYearSessionId"),
  );
  const schoolId = JSON.parse(localStorage.getItem("schoolId"));

  // ─── Check if direct edit from listing ──────────
  const isDirectEdit = !!state?.assessmentId;

  // ─── Fetch Grade Scale ──────────
  useEffect(() => {
    const fetchGradeScale = async () => {
      if (!gradeScaleId) return;
      try {
        const res = await axios.get(
          `/api/v1/performance/grade-scales?schoolId=${schoolId}`,
        );
        if (res.data.success && res.data.data) {
          const gradeScale = res.data.data.find(
            (scale) => scale._id === gradeScaleId || scale.id === gradeScaleId,
          );
          if (gradeScale && gradeScale.grades) {
            const transformedGrades = gradeScale.grades.map((g) => ({
              grade: g.grade,
              minMarks: g.minMarks || 0,
              maxMarks: g.maxMarks || 100,
              resultStatus: g.resultStatus || "Pass",
            }));
            setGradeOptions(transformedGrades);
          }
        }
      } catch (error) {
        console.log("Error fetching grade scale:", error);
      }
    };
    fetchGradeScale();
  }, [gradeScaleId]);

  const getMarksFromGrade = (gradeValue) => {
    const gradeObj = gradeOptions.find((g) => g.grade === gradeValue);
    return gradeObj ? gradeObj.maxMarks : 0;
  };

  // ─── Main Load Effect ──────────
  useEffect(() => {
    if (isDirectEdit) {
      setExistingAssessmentId(state.assessmentId);
      setMode(state.status === "completed" ? "completed" : "draft");
      loadExistingAssessment(state.assessmentId);
    } else {
      loadCreateMode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load Create Mode ──────────
  const loadCreateMode = async () => {
    if (!timeTableSessionId || !standard_id || !section_id) {
      setLoading(false);
      return;
    }

    try {
      // 1. Fetch students
      let students = [];
      const res = await axios.get(
        `/api/v1/school/student/getList?standardId=${standard_id}&sectionId=${section_id}&timetableSession=${timeTableSessionId}`,
      );
      res?.data?.data?.forEach((student) => {
        students.push({
          value: student.id,
          label: student.name,
          code: student.code,
          marks: "",
          remark: "",
          grade: "",
        });
      });
      students.sort(
        (a, b) => (parseInt(a.code) || 0) - (parseInt(b.code) || 0),
      );
      setStudentList(students);

      // 2. Check existing assessments
      const [draftRes, completedRes] = await Promise.all([
        axios.get(
          `/api/v1/assessment/filterByStandardSection/${standard_id}/${section_id}?status=draft`,
        ),
        axios.get(
          `/api/v1/assessment/filterByStandardSection/${standard_id}/${section_id}?status=completed`,
        ),
      ]);

      const draftAssessments = Array.isArray(draftRes?.data?.data)
        ? draftRes.data.data
        : Array.isArray(draftRes?.data)
          ? draftRes.data
          : [];

      const completedAssessments = Array.isArray(completedRes?.data?.data)
        ? completedRes.data.data
        : Array.isArray(completedRes?.data)
          ? completedRes.data
          : [];

      const allExisting = [...draftAssessments, ...completedAssessments];

      console.log("Draft:", draftRes?.data);
      console.log("Completed:", completedRes?.data);
      console.log("All Existing:", allExisting);

      // 3. Match by event + subject + type
      const matchingAssessment = allExisting.find((a) => {
        const aEventId =
          a.assessmentEventId?.id ||
          a.assessmentEventId?._id ||
          a.assessmentEventId;
        const aSubjectId =
          a.subject_id?.id || a.subject_id?._id || a.subject_id;
        const aTypeId =
          a.assessmentTypeId?.id ||
          a.assessmentTypeId?._id ||
          a.assessmentTypeId ||
          a.type_id?.id ||
          a.type_id?._id ||
          a.type_id;

        return (
          String(aEventId) === String(eventId) &&
          String(aSubjectId) === String(subject_id) &&
          String(aTypeId) === String(assessmentTypeId)
        );
      });

      console.log(matchingAssessment, "matchingAssessment");

      if (matchingAssessment) {
        const matchedId = matchingAssessment.id || matchingAssessment._id;
        const matchedStatus =
          matchingAssessment.status === "completed" ? "completed" : "draft";
        setExistingAssessmentId(matchedId);
        setMode(matchedStatus);
        await loadExistingAssessment(matchedId);
      } else {
        setMode("create");
        setLoading(false);
      }
    } catch (error) {
      console.log("Error in loadCreateMode:", error);
      setMode("create");
      setLoading(false);
    }
  };

  // ─── Load Existing Assessment ──────────
  const loadExistingAssessment = async (assessmentId) => {
    try {
      const res = await axios.get(
        `/api/v1/assessment/studentAssessment/getByAssessmentId/${assessmentId}`,
      );
      const data = res?.data?.data || [];
      data.sort(
        (a, b) =>
          (parseInt(a.student_id?.roll_number) || 0) -
          (parseInt(b.student_id?.roll_number) || 0),
      );
      setStudentListByAssessmentId(data);

      const initialSelected = data.map((student) => ({
        id: student?.student_id?.id,
        marks: student?.obtained_marks ?? 0,
        remark: student?.remark ?? "",
        grade: student?.obtained_grade ?? "",
        isEdited: false,
      }));
      setSelectedStudents(initialSelected);
      setOriginalSelectedStudents(JSON.parse(JSON.stringify(initialSelected)));
    } catch (error) {
      console.log(error);
      toast.error("Failed to load existing assessment");
    } finally {
      setLoading(false);
    }
  };

  // ─── Handle Change (New Assessment) ──────────
  const handleChange = (index, field, value) => {
    const updated = [...studentList];
    updated[index][field] = value;
    setStudentList(updated);

    const studentId = updated[index].value;
    const updatedMarksArray = [...selectedStudents];
    const existingIndex = updatedMarksArray.findIndex(
      (s) => s.id === studentId,
    );

    if (existingIndex !== -1) {
      updatedMarksArray[existingIndex] = {
        ...updatedMarksArray[existingIndex],
        marks:
          field === "marks" ? value : updatedMarksArray[existingIndex].marks,
        remark:
          field === "remark" ? value : updatedMarksArray[existingIndex].remark,
        grade:
          field === "grade" ? value : updatedMarksArray[existingIndex].grade,
      };
    } else {
      updatedMarksArray.push({
        id: studentId,
        marks: field === "marks" ? value : 0,
        remark: field === "remark" ? value : "",
        grade: field === "grade" ? value : "",
      });
    }
    setSelectedStudents(updatedMarksArray);
  };

  // ─── Hand Change Edit (Existing Assessment) ──────────
  const handChangeEdit = (index, field, value) => {
    const updated = [...studentListByAssessmentId];
    updated[index] = {
      ...updated[index],
      [field === "marks"
        ? "obtained_marks"
        : field === "grade"
          ? "obtained_grade"
          : "remark"]: value,
    };
    setStudentListByAssessmentId(updated);

    const studentId = updated[index]?.student_id?.id;
    let updatedMarksArray = [...selectedStudents];

    if (updatedMarksArray.length === 0) {
      updatedMarksArray = studentListByAssessmentId.map((student) => ({
        id: student?.student_id?.id,
        marks: student?.obtained_marks ?? 0,
        remark: student?.remark ?? "",
        grade: student?.obtained_grade ?? "",
        isEdited: false,
      }));
    }

    const existingIndex = updatedMarksArray.findIndex(
      (s) => s.id === studentId,
    );
    if (existingIndex !== -1) {
      const oldStudent = updatedMarksArray[existingIndex];
      updatedMarksArray[existingIndex] = {
        ...oldStudent,
        marks: field === "marks" ? value : oldStudent.marks,
        remark: field === "remark" ? value : oldStudent.remark,
        grade: field === "grade" ? value : oldStudent.grade,
        isEdited: true,
      };
    }
    setSelectedStudents(updatedMarksArray);
  };

  // ─── Handle Grade Change ──────────
  const handleGradeChange = (index, gradeValue, isExisting) => {
    const marks = getMarksFromGrade(gradeValue);

    if (isExisting) {
      const student = studentListByAssessmentId[index];
      const studentId = student?.student_id?.id;

      const updated = [...studentListByAssessmentId];
      updated[index] = {
        ...updated[index],
        obtained_grade: gradeValue,
        obtained_marks: marks,
      };
      setStudentListByAssessmentId(updated);

      setSelectedStudents((prev) => {
        const updatedMarksArray = [...prev];
        const existingIndex = updatedMarksArray.findIndex(
          (s) => s.id === studentId,
        );
        if (existingIndex !== -1) {
          updatedMarksArray[existingIndex] = {
            ...updatedMarksArray[existingIndex],
            marks: marks,
            grade: gradeValue,
            isEdited: true,
          };
        } else {
          updatedMarksArray.push({
            id: studentId,
            marks: marks,
            grade: gradeValue,
            remark: student?.remark ?? "",
            isEdited: true,
          });
        }
        return updatedMarksArray;
      });
    } else {
      const student = studentList[index];
      const studentId = student?.value;

      const updated = [...studentList];
      updated[index] = { ...updated[index], grade: gradeValue, marks: marks };
      setStudentList(updated);

      setSelectedStudents((prev) => {
        const updatedMarksArray = [...prev];
        const existingIndex = updatedMarksArray.findIndex(
          (s) => s.id === studentId,
        );
        if (existingIndex !== -1) {
          updatedMarksArray[existingIndex] = {
            ...updatedMarksArray[existingIndex],
            marks: marks,
            grade: gradeValue,
          };
        } else {
          updatedMarksArray.push({
            id: studentId,
            marks: marks,
            grade: gradeValue,
            remark: "",
          });
        }
        return updatedMarksArray;
      });
    }
  };

  // ─── Activate Re-evaluation ──────────
  const handleReevaluation = () => {
    setReevaluating(true);
    toast.success(
      `Re-evaluation activated. You can now edit marks/grades. Please provide reasons for changes.`,
    );
  };

  // ─── Validate Reasons ──────────
  const validateReasons = () => {
    if (!reevaluating || mode !== "completed") return true;

    const sourceList =
      mode !== "create" ? studentListByAssessmentId : studentList;
    let hasMissing = false;
    const missingStudents = [];

    sourceList.forEach((student, index) => {
      const studentId =
        mode !== "create" ? student?.student_id?.id : student?.value;
      const current = selectedStudents.find((s) => s.id === studentId);
      const original = originalSelectedStudents.find((s) => s.id === studentId);

      if (!current || !original) return;

      const marksChanged =
        entryType === "Marks" &&
        Number(original.marks ?? 0) !== Number(current.marks ?? 0);
      const gradeChanged =
        entryType === "Grade" &&
        (original.grade ?? "") !== (current.grade ?? "");

      if (
        (marksChanged || gradeChanged) &&
        (!current.remark || !current.remark.trim())
      ) {
        hasMissing = true;
        missingStudents.push(student?.student_id?.name || student?.label);
      }
    });

    if (hasMissing) {
      toast.error(
        `Reason required for changed students: ${missingStudents.join(", ")}`,
      );
      return false;
    }
    return true;
  };

  // ─── Build All Student Results ──────────
  const buildAllStudentResults = () => {
    return studentList.map((student) => ({
      teacher_id:
        assessmentDetails?.teacher_id || state?.assessment?.teacher_id?.id,
      student_id: student?.value,
      obtained_marks: Number(student?.marks ?? 0),
      remark: student?.remark ?? "",
      obtained_grade: student?.grade || "",
    }));
  };

  // ─── Handle Submit ──────────
  const handleOfflineSubmissionRequest = async (status) => {
    const isSubmit = status === "submit";
    const finalStatus = isSubmit ? "completed" : "draft";

    if (mode === "completed" && isSubmit && !reevaluating) {
      toast.error("Please activate re-evaluation first");
      return;
    }

    if (reevaluating && isSubmit && !validateReasons()) {
      return;
    }

    setIsInProgress(true);
    setDisableButton(true);
    setShowLoadingDialog(true);
    setLoadingMessage("Processing...");
    setLoadingProgress(0);

    let assessmentId = existingAssessmentId || state?.assessmentId;

    try {
      // Step 1: Create assessment (NEW only)
      if (!assessmentId) {
        setLoadingMessage("Creating assessment...");
        setLoadingProgress(15);

        let newAssessmentBody = {
          name: assessmentDetails?.name || state?.assessment?.name,
          description:
            assessmentDetails?.description || state?.assessment?.description,
          type_id: null,
          assessmentTypeId: assessmentTypeId,
          assessmentEventId: eventId,
          academicSessionId,
          timetableSessionId: timeTableSessionId,
          standard_id: standard_id,
          subject_id: subject_id,
          teacher_id:
            assessmentDetails?.teacher_id || state?.assessment?.teacher_id?.id,
          section_id: section_id,
          status: finalStatus,
          total_duration:
            assessmentDetails?.total_duration ||
            state?.assessment?.total_duration,
          deadline:
            assessmentDetails?.schedule_date ||
            state?.assessment?.schedule_date,
          schedule_date:
            assessmentDetails?.schedule_date ||
            state?.assessment?.schedule_date,
          schedule_time:
            assessmentDetails?.schedule_time ||
            state?.assessment?.schedule_time,
          add_to_live_session: null,
          questions: [],
          total_marks:
            assessmentDetails?.totalMarks || state?.assessment?.total_marks,
          offlineMode: true,
          entryType,
          gradeScaleId,
        };

        const res = await axios.post(
          `/api/v1/assessment/create`,
          newAssessmentBody,
        );
        assessmentId = res?.data?.data?.id;
        setExistingAssessmentId(assessmentId);
      }

      // Step 2: Add Students
      setLoadingMessage("Adding students...");
      setLoadingProgress(30);

      let students_id = [];
      const sourceList =
        assessmentId && mode !== "create"
          ? studentListByAssessmentId
          : studentList;

      for (const student of sourceList) {
        students_id.push(
          assessmentId && mode !== "create"
            ? student?.student_id?.id
            : student?.value,
        );
      }

      await axios.post(
        `/api/v1/assessment/${assessmentId}/studentAssessment/addStudents`,
        {
          teacher_id:
            assessmentDetails?.teacher_id || state?.assessment?.teacher_id?.id,
          student_id: students_id,
          status: "completed",
          deadline:
            assessmentDetails?.schedule_date ||
            state?.assessment?.schedule_date,
        },
      );

      // Step 3: Bulk Results
      setLoadingProgress(50);
      setLoadingMessage("Saving student results...");

      let studentResults = [];

      if (assessmentId && mode !== "create") {
        // Existing: ONLY edited students (or all if submit with no edits)
        const editedStudents = selectedStudents.filter((s) => s.isEdited);

        if (editedStudents.length === 0 && isSubmit) {
          studentResults = studentListByAssessmentId.map((s) => ({
            id: s?.student_id?.id,
            marks: s?.obtained_marks ?? 0,
            grade: s?.obtained_grade ?? "",
            remark: s?.remark ?? "",
          }));
        } else if (reevaluating && isSubmit) {
          // Re-evaluation: only changed students
          studentResults = [];
          studentListByAssessmentId.forEach((s) => {
            const studentId = s?.student_id?.id;
            const current = selectedStudents.find((st) => st.id === studentId);
            const original = originalSelectedStudents.find(
              (st) => st.id === studentId,
            );

            if (!current || !original) return;

            const marksChanged =
              entryType === "Marks" &&
              Number(original.marks ?? 0) !== Number(current.marks ?? 0);
            const gradeChanged =
              entryType === "Grade" &&
              (original.grade ?? "") !== (current.grade ?? "");

            if (marksChanged || gradeChanged) {
              studentResults.push({
                id: studentId,
                marks: current.marks ?? 0,
                grade: current.grade ?? "",
                remark: current.remark ?? "",
              });
            }
          });
        } else {
          studentResults = editedStudents;
        }
      } else {
        // New: ALL students
        studentResults =
          selectedStudents.length > 0
            ? selectedStudents
            : studentList.map((s) => ({
                id: s.value,
                marks: s.marks || 0,
                grade: s.grade || "",
                remark: s.remark || "",
              }));
      }

      const bulkPayload = {
        students: studentResults.map((student) => {
          const result = {
            id: null,
            student_id: student.id || student.student_id,
            remark: student.remark ?? "",
          };
          if (entryType === "Marks") {
            result.obtained_marks = Number(
              student.marks ?? student.obtained_marks ?? 0,
            );
          } else {
            result.obtained_grade =
              student.grade || student.obtained_grade || "";
          }
          return result;
        }),
        teacher_id: null,
      };

      if (assessmentId && mode !== "create") {
        await axios.put(
          `/api/v1/assessment/${assessmentId}/studentResult/updateBulk`,
          bulkPayload,
        );
      } else {
        await axios.post(
          `/api/v1/assessment/${assessmentId}/studentResult/createBulk`,
          bulkPayload,
        );
      }

      // Step 4: Final Update (EXISTING + SUBMIT only)
      if (assessmentId && mode !== "create" && isSubmit) {
        setLoadingProgress(85);
        setLoadingMessage("Finalizing assessment...");

        let finalizeBody = {
          name: assessmentDetails?.name || state?.assessment?.name,
          description:
            assessmentDetails?.description || state?.assessment?.description,
          type_id: null,
          assessmentTypeId: assessmentTypeId,
          assessmentEventId: eventId,
          academicSessionId,
          timetableSessionId: timeTableSessionId,
          standard_id: standard_id,
          subject_id: subject_id,
          teacher_id:
            assessmentDetails?.teacher_id || state?.assessment?.teacher_id?.id,
          section_id: section_id,
          status: "completed",
          total_duration:
            assessmentDetails?.total_duration ||
            state?.assessment?.total_duration,
          deadline:
            assessmentDetails?.schedule_date ||
            state?.assessment?.schedule_date,
          schedule_date:
            assessmentDetails?.schedule_date ||
            state?.assessment?.schedule_date,
          schedule_time:
            assessmentDetails?.schedule_time ||
            state?.assessment?.schedule_time,
          add_to_live_session: null,
          questions: [],
          total_marks:
            assessmentDetails?.totalMarks || state?.assessment?.total_marks,
          offlineMode: true,
          entryType,
          gradeScaleId,
        };

        await axios.put(
          `/api/v1/assessment/update/${assessmentId}`,
          finalizeBody,
        );
      }

      setLoadingProgress(100);
      setShowLoadingDialog(false);
      toast.success(
        isSubmit ? "Assessment submitted successfully!" : "Draft saved!",
      );

      if (isSubmit) {
        setMode("completed");
        setReevaluating(false);
      } else {
        setMode("draft");
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "Something went wrong");
      setShowLoadingDialog(false);
    } finally {
      setIsInProgress(false);
      setDisableButton(false);
      navigate("/offline-performance/academic");
      sessionStorage.removeItem("localStorage");
    }
  };

  // ─── Render Marks or Grade Input ──────────
  const renderMarksOrGradeInput = (index, student, isExisting) => {
    const currentMarks = isExisting
      ? (student?.obtained_marks ?? 0)
      : (student?.marks ?? 0);
    const currentGrade = isExisting
      ? (student?.obtained_grade ?? "")
      : (student?.grade ?? "");
    const totalMarks =
      assessmentDetails?.totalMarks || state?.assessment?.total_marks || 100;

    // Read-only when completed and NOT reevaluating
    const isReadOnly = mode === "completed" && !reevaluating;

    // GRADE MODE
    if (entryType === "Grade") {
      return (
        <div style={{ width: "100%" }}>
          <select
            className="form-select grade-dropdown"
            value={currentGrade || ""}
            onChange={(e) => {
              const selectedGrade = e.target.value;
              if (selectedGrade) {
                handleGradeChange(index, selectedGrade, isExisting);
              }
            }}
            disabled={isReadOnly}
            style={{
              width: "100%",
              padding: "6px 8px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              fontSize: "14px",
              backgroundColor: isReadOnly ? "#f5f5f5" : "white",
              cursor: isReadOnly ? "not-allowed" : "pointer",
              minHeight: "38px",
            }}
          >
            <option value="">Select Grade</option>
            {gradeOptions.map((g, idx) => (
              <option key={idx} value={g.grade}>
                {g.grade} ({g.minMarks}-{g.maxMarks})
              </option>
            ))}
          </select>
          {currentGrade && (
            <div
              style={{ fontSize: "11px", color: "#6c757d", marginTop: "2px" }}
            >
              Selected: {currentGrade} | Marks:{" "}
              {getMarksFromGrade(currentGrade)}
            </div>
          )}
        </div>
      );
    }

    // MARKS MODE
    return (
      <input
        type="text"
        value={currentMarks ?? ""}
        onFocus={(e) => {
          if (e.target.value === "0") e.target.value = "";
        }}
        onChange={(e) => {
          let value = e.target.value;
          if (!/^\d*$/.test(value)) return;
          const numValue = value === "" ? 0 : parseInt(value, 10);
          if (numValue > totalMarks) value = String(totalMarks);

          if (isExisting) {
            handChangeEdit(index, "marks", value);
          } else {
            handleChange(index, "marks", value);
          }
        }}
        readOnly={isReadOnly}
        style={{
          width: "100%",
          padding: "6px 8px",
          borderRadius: "6px",
          border: "1px solid #ddd",
          fontSize: "14px",
          backgroundColor: isReadOnly ? "#f5f5f5" : "white",
        }}
      />
    );
  };

  // ─── Render Remark Input ──────────
  const renderRemarkInput = (index, student, isExisting) => {
    const isReadOnly = mode === "completed" && !reevaluating;
    const value = isExisting ? (student?.remark ?? "") : student?.remark || "";
    const studentId = isExisting ? student?.student_id?.id : student?.value;

    // Show reason field when re-evaluating and marks/grade changed
    let showReason = false;
    let isReasonRequired = false;

    if (reevaluating && mode === "completed" && isExisting) {
      const current = selectedStudents.find((s) => s.id === studentId);
      const original = originalSelectedStudents.find((s) => s.id === studentId);

      if (current && original) {
        const marksChanged =
          entryType === "Marks" &&
          Number(original.marks ?? 0) !== Number(current.marks ?? 0);
        const gradeChanged =
          entryType === "Grade" &&
          (original.grade ?? "") !== (current.grade ?? "");

        if (marksChanged || gradeChanged) {
          showReason = true;
          isReasonRequired = true;
        }
      }
    }

    return (
      <div style={{ width: "100%" }}>
        <textarea
          rows="1"
          value={value}
          onChange={(e) => {
            if (isExisting) {
              handChangeEdit(index, "remark", e.target.value);
            } else {
              handleChange(index, "remark", e.target.value);
            }
          }}
          disabled={isReadOnly}
          maxLength={250}
          style={{
            width: "100%",
            padding: "6px 8px",
            borderRadius: "6px",
            border: "1px solid #ddd",
            fontSize: "14px",
            resize: "vertical",
            minHeight: "38px",
            backgroundColor: isReadOnly ? "#f5f5f5" : "white",
          }}
        />
        {showReason && (
          <div style={{ marginTop: "4px" }}>
            <div
              style={{
                fontSize: "10px",
                color: "#6c757d",
                marginBottom: "2px",
              }}
            >
              Reason for change
              {isReasonRequired && <span style={{ color: "#dc3545" }}> *</span>}
            </div>
            <input
              type="text"
              value={
                selectedStudents.find((s) => s.id === studentId)?.remark || ""
              }
              onChange={(e) => {
                handChangeEdit(index, "remark", e.target.value);
              }}
              placeholder="Enter reason for change..."
              style={{
                width: "100%",
                padding: "6px 8px",
                borderRadius: "6px",
                border: `1px solid ${
                  isReasonRequired &&
                  !selectedStudents.find((s) => s.id === studentId)?.remark
                    ? "#dc3545"
                    : "#ddd"
                }`,
                fontSize: "13px",
                minHeight: "32px",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const handleNavigate = () => {
    if (state?.assessmentId || existingAssessmentId) {
      navigate("/offline-performance/academic");
    } else {
      navigate("/add-report");
    }
  };

  // ─── Get Mode Badge ──────────
  const getModeBadge = () => {
    if (mode === "completed") {
      return (
        <span
          className="badge px-3 py-2"
          style={{
            backgroundColor: "#28a745",
            color: "white",
            fontSize: "13px",
            fontWeight: "500",
            borderRadius: "20px",
          }}
        >
          <CheckCircle2 size={14} style={{ marginRight: "6px" }} />
          Completed
        </span>
      );
    }
    if (mode === "draft") {
      return (
        <span
          className="badge px-3 py-2"
          style={{
            backgroundColor: "#ffc107",
            color: "black",
            fontSize: "13px",
            fontWeight: "500",
            borderRadius: "20px",
          }}
        >
          <FileClock size={14} style={{ marginRight: "6px" }} />
          Draft
        </span>
      );
    }
    return (
      <span
        className="badge px-3 py-2"
        style={{
          backgroundColor: "#0d6efd",
          color: "white",
          fontSize: "13px",
          fontWeight: "500",
          borderRadius: "20px",
        }}
      >
        <FileText size={14} style={{ marginRight: "6px" }} />
        Create Mode
      </span>
    );
  };

  // ─── Determine which list to show ──────────
  const isExistingView =
    mode === "draft" ||
    mode === "completed" ||
    (mode === "create" && existingAssessmentId);
  const displayList =
    isExistingView && studentListByAssessmentId.length > 0
      ? studentListByAssessmentId
      : studentList;

  return (
    <div
      className="create_wrap w-100 px-3 orange-scrollBar"
      style={{ overflowY: "scroll", height: "calc(100vh - 10px)" }}
    >
      {/* ─── Loading Dialog ────────── */}
      <Dialog
        open={showLoadingDialog}
        disableEscapeKeyDown
        PaperProps={{
          style: {
            borderRadius: "20px",
            padding: "0",
            minWidth: "420px",
            maxWidth: "460px",
            boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              height: "4px",
              width: "100%",
              background:
                "linear-gradient(90deg, #f97316, #fb923c, #fbbf24, #f97316)",
              backgroundSize: "300% 100%",
              animation: "shimmer 2s infinite linear",
            }}
          />
          <Box sx={{ textAlign: "center", py: 4, px: 4 }}>
            <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
              <CircularProgress
                variant="determinate"
                value={loadingProgress}
                size={80}
                thickness={3.5}
                sx={{
                  color: "#f97316",
                  "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#f97316", fontSize: "18px" }}
                >
                  {loadingProgress}%
                </Typography>
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                mb: 0.5,
                fontSize: "17px",
              }}
            >
              {loadingMessage || "Processing..."}
            </Typography>
            <Box sx={{ width: "100%", mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 8,
                  borderRadius: "10px",
                  backgroundColor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "10px",
                    background: "linear-gradient(90deg, #f97316, #fb923c)",
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Header ────────── */}
      <div className="mb-2 cursor-pointer" onClick={handleNavigate}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ cursor: "pointer" }}
        >
          <div>
            <img src="/images/Back Arrow.svg" alt="" />
          </div>
          <div className="d-flex flex-column gap-1">
            <span className="head_manual">Student Marks Entry (Offline)</span>
            <span className="fw-small text-muted" style={{ fontSize: "10px" }}>
              {state?.assessmentId || existingAssessmentId
                ? "Dashboard / Performance / Review / Student Marks Entry (Offline)"
                : "Dashboard / Performance / Student Marks Entry (Offline)"}
            </span>
          </div>
        </div>
      </div>

      <div
        className="w-100 py-2 px-4 rounded-3"
        style={{ backgroundColor: "#fff", height: "100%" }}
      >
        {/* ─── Class Info ────────── */}
        <div className="class-section-subject d-flex align-items-start flex-column gap-2 ml-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="fs-4 poppins-semibold">
              <span>
                {assessmentDetails?.standard_id?.name
                  ? assessmentDetails?.standard_id?.name
                  : studentListByAssessmentId[0]?.assessment_id?.standard
                      ?.name}{" "}
                {` - `}
                {assessmentDetails?.section_id?.name
                  ? assessmentDetails?.section_id?.name
                  : studentListByAssessmentId[0]?.assessment_id?.section?.name}
              </span>
              {" | "}
              <span>
                {assessmentDetails?.subject_id?.name
                  ? assessmentDetails?.subject_id?.name
                  : studentListByAssessmentId[0]?.assessment_id?.subject?.name}
              </span>
            </div>

            <span
              className="badge"
              style={{
                backgroundColor: "#8D4B28",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: "4px",
              }}
            >
              <Trophy size={12} style={{ marginRight: "4px" }} />
              {eventName}
            </span>

            <span
              className="badge"
              style={{
                backgroundColor:
                  "#" +
                  (state?.assessment?.subject_id?.subject_color || "000000"),
                color: "#fff",
                fontSize: "11px",
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: "4px",
              }}
            >
              <FileText size={12} style={{ marginRight: "4px" }} />
              {typeName}
            </span>

            <span
              className="badge px-3 py-2"
              style={{
                backgroundColor: entryType === "Marks" ? "#28a745" : "#ffc107",
                color: entryType === "Marks" ? "white" : "black",
                fontSize: "13px",
                fontWeight: "500",
                borderRadius: "20px",
              }}
            >
              {entryType === "Marks" ? "📝 Marks Entry" : "📊 Grades Entry"}
            </span>

            {getModeBadge()}

            {mode === "completed" && !reevaluating && (
              <button
                className="btn btn-sm"
                onClick={handleReevaluation}
                style={{
                  backgroundColor: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                  fontSize: "12px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Unlock size={14} /> Activate Re-evaluation
              </button>
            )}

            {reevaluating && (
              <span
                className="badge px-3 py-2"
                style={{
                  backgroundColor: "#dbeafe",
                  color: "#1e40af",
                  fontSize: "12px",
                  fontWeight: "600",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Unlock size={14} /> Re-evaluating
              </span>
            )}
          </div>
        </div>

        {/* ─── Loading State ────────── */}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* ─── Table ────────── */}
            <div className="mid-student-table">
              <div className="table-container-offlineSub">
                <div className="scrollable-table-wrapper">
                  <table className="student-table-offlineSub">
                    <thead style={{ position: "sticky", top: "0" }}>
                      <tr>
                        <th style={{ width: "22%" }}>Name</th>
                        <th style={{ width: "12%" }}>Roll No.</th>
                        <th style={{ width: "66%" }}>
                          {entryType === "Marks"
                            ? "Obtained Marks"
                            : "Obtained Grade"}
                          <div className="sub-header-offlineSub">
                            <div className="heading-text">
                              Total {entryType === "Marks" ? "Marks" : "Grades"}
                              :{" "}
                              {entryType === "Marks"
                                ? assessmentDetails?.totalMarks ||
                                  studentListByAssessmentId[0]?.assessment_id
                                    ?.total_marks ||
                                  0
                                : "Select Grade"}
                            </div>
                            <div className="heading-text">
                              Duration:{" "}
                              {assessmentDetails?.total_duration ||
                                studentListByAssessmentId[0]?.assessment_id
                                  ?.total_duration ||
                                "N/A"}{" "}
                              min
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {displayList.length === 0 ? (
                        <tr>
                          <td
                            colSpan="3"
                            className="text-center py-4 text-muted"
                          >
                            No students found
                          </td>
                        </tr>
                      ) : (
                        displayList.map((student, index) => {
                          const studentId = isExistingView
                            ? student?.student_id?.id
                            : student?.value;

                          let showReason = false;
                          let isReasonRequired = false;
                          let reasonValue = "";

                          if (
                            mode === "completed" &&
                            reevaluating &&
                            isExistingView
                          ) {
                            const current = selectedStudents.find(
                              (s) => s.id === studentId,
                            );
                            const original = originalSelectedStudents.find(
                              (s) => s.id === studentId,
                            );

                            if (current && original) {
                              const marksChanged =
                                entryType === "Marks" &&
                                Number(original.marks ?? 0) !==
                                  Number(current.marks ?? 0);
                              const gradeChanged =
                                entryType === "Grade" &&
                                (original.grade ?? "") !==
                                  (current.grade ?? "");

                              if (marksChanged || gradeChanged) {
                                showReason = true;
                                isReasonRequired = true;
                                reasonValue = current.remark || "";
                              }
                            }
                          }

                          return (
                            <tr key={index}>
                              <td style={{ width: "22%" }}>
                                {isExistingView
                                  ? student?.student_id?.name
                                  : student?.label}
                              </td>
                              <td style={{ width: "12%" }}>
                                {isExistingView
                                  ? student?.student_id?.roll_number
                                  : student?.code}
                              </td>
                              <td style={{ width: "66%" }}>
                                {renderMarksOrGradeInput(
                                  index,
                                  student,
                                  isExistingView,
                                )}

                                {showReason && (
                                  <div style={{ marginTop: "6px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        color: "#374151",
                                        marginBottom: "3px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      Reason for change
                                      {isReasonRequired && (
                                        <span style={{ color: "#dc3545" }}>
                                          *
                                        </span>
                                      )}
                                      {isReasonRequired && !reasonValue && (
                                        <span
                                          style={{
                                            fontSize: "10px",
                                            color: "#dc3545",
                                            fontWeight: "500",
                                          }}
                                        >
                                          (required)
                                        </span>
                                      )}
                                    </div>
                                    <input
                                      type="text"
                                      value={reasonValue}
                                      onChange={(e) => {
                                        handChangeEdit(
                                          index,
                                          "remark",
                                          e.target.value,
                                        );
                                      }}
                                      placeholder="Enter reason for change..."
                                      style={{
                                        width: "100%",
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        border: `1px solid ${
                                          isReasonRequired && !reasonValue
                                            ? "#dc3545"
                                            : "#d1d5db"
                                        }`,
                                        fontSize: "13px",
                                        backgroundColor: "#fff",
                                        outline: "none",
                                      }}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ─── Footer Buttons ────────── */}
            <div className="d-flex justify-content-end gap-2 mr-3 mt-3">
              {/* Draft/Save Button */}
              {mode !== "completed" && (
                <div>
                  <button
                    className="btn offline-sub-Btn"
                    style={{
                      backgroundColor: "#fff7ed",
                      color: "#c2410c",
                      border: "1px solid #fdba74",
                    }}
                    onClick={() => handleOfflineSubmissionRequest("save")}
                    disabled={isInProgress || disableButton}
                  >
                    {isInProgress ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Save size={16} />
                    )}{" "}
                    {mode === "draft" ? "Update Draft" : "Save Draft"}
                  </button>
                </div>
              )}

              {/* Re-evaluation Submit */}
              {mode === "completed" && reevaluating && (
                <div>
                  <button
                    className="btn offline-sub-Btn"
                    style={{
                      backgroundColor: "#2563eb",
                      color: "white",
                    }}
                    onClick={() => handleOfflineSubmissionRequest("submit")}
                    disabled={isInProgress || disableButton}
                  >
                    {isInProgress ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <RefreshCw size={16} />
                    )}{" "}
                    Submit Re-evaluation
                  </button>
                </div>
              )}

              {/* Normal Submit */}
              {mode !== "completed" && (
                <div>
                  <button
                    className="btn offline-sub-Btn"
                    onClick={() => handleOfflineSubmissionRequest("submit")}
                    disabled={isInProgress || disableButton}
                  >
                    {isInProgress ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}{" "}
                    Submit
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
}

export default OfflineSubDraftCompleted;
